import { addYears, isAfter, isBefore, isSameDay, isSameYear, parse } from 'date-fns';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { AnalyticsTrigger, Button, IButtonProps } from 'telia-front-react';
import AddressAutocomplete from '../../address/AddressAutocomplete';
import { resolveAddressCode } from '../../address/AddressUtil';
import { Address, Room } from '../../stores/AddressStore';
import InternetCalculatorStore from '../../stores/InternetCalculatorStore';
import { TranslationsContext } from '@teliaee/telia-translate';
import SegmentStore, { SEGMENT_BUSINESS } from '../../stores/SegmentStore';
import { withAnalytics } from '../../util/AnalyticsUtil';
import { ProductCard } from './InternetCalculatorInterfaces';

export interface InternetAddressSearchProps {
  calculatorKey: string;
  asButton?: boolean;
  buttonProps?: IButtonProps;
  buttonLabel?: string;
  inCalculator?: boolean;
  internetCalculatorStore?: InternetCalculatorStore;
  segmentStore?: SegmentStore;
  pushAnalytics?: (id: string) => {};
}

@observer
class InternetAddressSearch extends React.Component<InternetAddressSearchProps> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;
  private addAddressToStore = (address: Address, room: Room) => {
    const { internetCalculatorStore, calculatorKey, inCalculator, segmentStore } = this.props;

    if (address) {
      internetCalculatorStore.setAddress(calculatorKey, address, room, inCalculator, segmentStore.currentSegment).then(() => {
        this.analyticsPush(internetCalculatorStore, address, room, calculatorKey);
      });
    }
  };

  private analyticsPush(internetCalculatorStore: InternetCalculatorStore, address: Address, room: Room, calculatorKey: string) {
    this.props.pushAnalytics(`Show - Address - ${resolveAddressCode(address, room)}`);
    const offersByLocation = internetCalculatorStore.getState(calculatorKey).internetOffers.teliaOffers
      ? internetCalculatorStore.getState(calculatorKey).internetOffers.teliaOffers.slice()
      : [];
    const plannedNetworkOffer = offersByLocation.find((offer) => offer.plannedNetwork);
    const isNetworkPlannedWithinAYear = plannedNetworkOffer && this.isNetworkPlannedWithinAYear(plannedNetworkOffer);
    if (offersByLocation.length === 0) {
      this.props.pushAnalytics('Show - Technology - None');
    } else {
      const firstProductOfferCode = offersByLocation[0].productOfferingCode;
      this.props.pushAnalytics(`Show - Technology - ${firstProductOfferCode}`);
    }
    if (isNetworkPlannedWithinAYear) {
      this.props.pushAnalytics('Show - Technology - Building');
    }
  }

  private isNetworkPlannedWithinAYear(plannedNetworkOffer: ProductCard) {
    const yearFromNow = addYears(Date.now(), 1);
    if (plannedNetworkOffer.timeline === 'EXACT_DATE') {
      const plannedNetworkDate = parse(plannedNetworkOffer.date, 'dd.MM.yyyy', Date.now());
      return (
        (isSameDay(plannedNetworkDate, Date.now()) || isAfter(plannedNetworkDate, Date.now())) &&
        (isSameDay(plannedNetworkDate, yearFromNow) || isBefore(plannedNetworkDate, yearFromNow))
      );
    }
    const plannedNetworkYear = parse(plannedNetworkOffer.year, 'yyyy', Date.now());
    return (
      (isSameYear(plannedNetworkYear, Date.now()) || isAfter(plannedNetworkYear, Date.now())) &&
      (isSameYear(plannedNetworkYear, yearFromNow) || isBefore(plannedNetworkYear, yearFromNow))
    );
  }

  public render() {
    const translate = this.context.translate;
    const { internetCalculatorStore, calculatorKey, segmentStore } = this.props;

    if (this.props.asButton) {
      return (
        <AnalyticsTrigger trigger={{ id: 'Link - Aadressi otsing', callback: 'onClick' }}>
          <Button
            {...this.props.buttonProps}
            text={translate(this.props.buttonLabel)}
            onClick={() => internetCalculatorStore.setCalculatorOpen(calculatorKey, true, true)}
          />
        </AnalyticsTrigger>
      );
    }

    return (
      <AddressAutocomplete
        labelsPrefix="internetAndTvCalculator"
        roomLabel={segmentStore.currentSegment === SEGMENT_BUSINESS ? 'internetAndTvCalculator.addressAutocomplete.room.B2B' : null}
        onSubmit={this.addAddressToStore}
      />
    );
  }
}

export default inject('internetCalculatorStore', 'segmentStore')(withAnalytics(InternetAddressSearch));
